import React, { useEffect, useRef, useState } from "react";
import { Gift, X } from "lucide-react";
import api from "@/lib/api";
import { useLang } from "@/context/LangContext";

const STORAGE_KEY = "kaos-gift-popup-seen";
const FOURTEEN_DAYS = 14 * 24 * 60 * 60 * 1000;

const GiftPopup = () => {
  const { lang } = useLang();
  const [open, setOpen] = useState(false);
  const [phone, setPhone] = useState("");
  const [consent, setConsent] = useState(false);
  const [loading, setLoading] = useState(false);
  const [complete, setComplete] = useState(false);
  const inputRef = useRef(null);

  const copy = lang === "it" ? {
    eyebrow: "Un regalo per te",
    title: "Benvenuti nel mondo Kaós",
    body: "Lascia il tuo numero WhatsApp: ti contatteremo con i dettagli del regalo riservato al tuo primo ordine.",
    placeholder: "+39 380 000 0000",
    consent: "Acconsento a essere contattato/a su WhatsApp da Kaós Luxury Cosmetics.",
    cta: "Ricevi il mio regalo",
    later: "Magari più tardi",
    success: "Grazie! Ti contatteremo presto su WhatsApp con il tuo regalo.",
    error: "Non è stato possibile registrare il numero. Riprova tra poco.",
  } : {
    eyebrow: "A gift for you",
    title: "Welcome to the Kaós world",
    body: "Leave your WhatsApp number and we'll contact you with details of the gift reserved for your first order.",
    placeholder: "+39 380 000 0000",
    consent: "I agree to be contacted on WhatsApp by Kaós Luxury Cosmetics.",
    cta: "Claim my gift",
    later: "Maybe later",
    success: "Thank you! We'll contact you soon on WhatsApp with your gift.",
    error: "We couldn't save your number. Please try again shortly.",
  };

  useEffect(() => {
    const lastSeen = Number(window.localStorage.getItem(STORAGE_KEY) || 0);
    if (Date.now() - lastSeen < FOURTEEN_DAYS) return undefined;
    const timer = window.setTimeout(() => setOpen(true), 900);
    return () => window.clearTimeout(timer);
  }, []);

  useEffect(() => {
    if (!open) return undefined;
    const previous = document.activeElement;
    window.setTimeout(() => inputRef.current?.focus(), 50);
    const onKeyDown = (event) => {
      if (event.key === "Escape") close();
    };
    document.addEventListener("keydown", onKeyDown);
    return () => {
      document.removeEventListener("keydown", onKeyDown);
      previous?.focus?.();
    };
  }, [open]);

  const close = () => {
    window.localStorage.setItem(STORAGE_KEY, String(Date.now()));
    setOpen(false);
  };

  const submit = async (event) => {
    event.preventDefault();
    setLoading(true);
    try {
      await api.post("/gift-leads", { phone, consent, website: "" });
      window.localStorage.setItem(STORAGE_KEY, String(Date.now()));
      setComplete(true);
    } catch {
      window.alert(copy.error);
    } finally {
      setLoading(false);
    }
  };

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center bg-charcoal/55 px-4 py-8"
      role="dialog"
      aria-modal="true"
      aria-labelledby="gift-popup-title"
      onMouseDown={(event) => event.target === event.currentTarget && close()}
    >
      <div className="relative w-full max-w-lg overflow-hidden bg-[#f5efe8] shadow-2xl">
        <button
          type="button"
          onClick={close}
          className="absolute right-4 top-4 z-10 grid h-10 w-10 place-items-center text-charcoal hover:bg-white/70"
          aria-label="Chiudi"
        >
          <X size={20} />
        </button>
        <div className="grid grid-cols-1 sm:grid-cols-[145px_1fr]">
          <div className="hidden sm:flex bg-charcoal text-white items-center justify-center p-8">
            <Gift size={52} strokeWidth={1} className="text-champagne" />
          </div>
          <div className="p-7 sm:p-9">
            {complete ? (
              <div className="py-8 text-center sm:text-left">
                <Gift size={36} strokeWidth={1.2} className="text-champagne mb-5 mx-auto sm:mx-0" />
                <p className="font-serif text-2xl text-charcoal leading-snug">{copy.success}</p>
                <button type="button" onClick={close} className="mt-7 text-xs uppercase tracking-[0.2em] border-b border-charcoal pb-1">
                  OK
                </button>
              </div>
            ) : (
              <>
                <p className="text-[10px] uppercase tracking-[0.24em] text-stone-500">{copy.eyebrow}</p>
                <h2 id="gift-popup-title" className="font-serif text-3xl leading-tight text-charcoal mt-3">{copy.title}</h2>
                <p className="text-sm leading-relaxed text-stone-600 mt-4">{copy.body}</p>
                <form onSubmit={submit} className="mt-6 space-y-4">
                  <input type="text" name="website" tabIndex="-1" autoComplete="off" className="hidden" aria-hidden="true" />
                  <label className="block">
                    <span className="sr-only">WhatsApp</span>
                    <input
                      ref={inputRef}
                      type="tel"
                      value={phone}
                      onChange={(event) => setPhone(event.target.value)}
                      required
                      minLength={7}
                      maxLength={30}
                      placeholder={copy.placeholder}
                      className="h-12 w-full border border-stone-300 bg-white px-4 text-sm outline-none focus:border-charcoal"
                    />
                  </label>
                  <label className="flex items-start gap-3 text-[11px] leading-relaxed text-stone-600">
                    <input type="checkbox" checked={consent} onChange={(event) => setConsent(event.target.checked)} required className="mt-0.5" />
                    <span>{copy.consent}</span>
                  </label>
                  <button disabled={loading} type="submit" className="w-full bg-charcoal px-5 py-3.5 text-xs uppercase tracking-[0.16em] text-white hover:bg-stone-800 disabled:opacity-50">
                    {loading ? "…" : copy.cta}
                  </button>
                </form>
                <button type="button" onClick={close} className="mt-4 w-full text-xs text-stone-500 underline underline-offset-4">
                  {copy.later}
                </button>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default GiftPopup;
